// =============================================================================
// ProtectedRoute — Route guard that requires authentication
// =============================================================================
// Wraps routes that need a logged-in user. Redirects to /login if not
// authenticated. Optionally checks for specific permissions via RBAC.
//
// Organization membership is NOT enforced here — it is handled via
// an optional banner/prompt on the dashboard (non-blocking UX).
// =============================================================================

import { Navigate, useLocation, Outlet } from 'react-router';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { Spinner, Card, Button } from '@tkpod/ui';

interface ProtectedRouteProps {
  /**
   * Optional permission string(s) required to access the route.
   * If provided, the user must have ANY of the listed permissions (OR logic).
   */
  requiredPermissions?: string[];

  /** Custom redirect path. @default '/login' */
  redirectTo?: string;

  /** Children override (if not using Outlet) */
  children?: React.ReactNode;
}

export function ProtectedRoute({
  requiredPermissions,
  redirectTo = '/login',
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not authenticated — redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // Authenticated but no organization membership (suspended/removed)
  if (user && !user.currentOrganization) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <h2 className="text-xl font-bold text-text-primary mb-2">
              No Active Membership
            </h2>
            <p className="text-sm text-text-secondary mb-6">
              You are not currently a member of any organization. This may be because
              your membership was suspended or removed. Please contact your organization admin.
            </p>
            <Button variant="outline" onClick={() => void logout()}>
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Check permissions — uses hasAnyPermission so :manage wildcard works
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = user?.currentOrganization?.permissions ?? [];
    if (!hasAnyPermission(userPermissions, requiredPermissions)) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold text-text-primary">Access Denied</h1>
          <p className="text-text-secondary">
            You do not have the required permissions to access this page.
          </p>
        </div>
      );
    }
  }

  return children ? <>{children}</> : <Outlet />;
}
